export interface WireFrame {
  seq: number;
  ts: number;
  kind: "frame";
  direction: "in" | "out";
  protocol: string;
  transport: "http" | "sse";
  method: string | null;
  payload: unknown;
  bytes: number;
  headers?: Record<string, string>;
}

export interface GateEvent {
  seq: number;
  ts: number;
  kind: "gate";
  gate: string;
  decision: "allowed" | "denied" | "held_for_approval";
  detail: string;
  toolName?: string;
}

export interface LogEvent {
  seq: number;
  ts: number;
  kind: "log";
  channel: "sandbox" | "host" | "server";
  level: string;
  message: string;
  data?: unknown;
}

export interface StateEvent {
  seq: number;
  ts: number;
  kind: "state";
  what: string;
  value: unknown;
}

export type LabEvent = WireFrame | GateEvent | LogEvent | StateEvent;

export type GateName = "identity" | "privilege" | "catalog" | "egress" | "audit";
export type ModeName = "deferred" | "poison" | "scripted" | "legacy";

export interface GatesResponse {
  gates: Record<GateName, boolean>;
  labels: Record<GateName, string>;
  pins: number;
}

export interface ModesResponse {
  modes: Record<ModeName, boolean>;
  live: boolean;
  hasModelAccess: boolean;
  model: string;
  version: string;
  protocolVersions: string[];
  attacksEnabled: boolean;
}

export interface CatalogTool {
  name: string;
  title: string;
  description: string;
  userFacing: string;
  inputSchema: Record<string, unknown>;
  outputSchema?: Record<string, unknown>;
  annotations?: Record<string, boolean>;
  group: "core" | "corp" | "meta" | "demo";
  scopes: string[];
  hidden: boolean;
  hash: string;
}

export interface AuditRow {
  id: number;
  ts: number;
  agent: string | null;
  protocol: string;
  method: string;
  tool_name: string | null;
  args_json: string | null;
  result_summary: string | null;
  decision: string;
  gate: string | null;
  duration_ms: number | null;
}

async function req<T>(path: string, init?: RequestInit): Promise<T> {
  const res = await fetch(path, {
    headers: init?.body ? { "content-type": "application/json" } : undefined,
    ...init,
  });
  if (!res.ok && res.status >= 500) throw new Error(`${path} → ${res.status}`);
  return (await res.json()) as T;
}

export const api = {
  gates: () => req<GatesResponse>("/api/gates"),
  setGates: (patch: Partial<Record<GateName | "reset", boolean>>) =>
    req<GatesResponse>("/api/gates", { method: "POST", body: JSON.stringify(patch) }),

  modes: () => req<ModesResponse>("/api/modes"),
  setModes: (patch: Partial<Record<ModeName, boolean>>) =>
    req<ModesResponse>("/api/modes", { method: "POST", body: JSON.stringify(patch) }),

  catalog: (opts: { deferred?: boolean; hidden?: boolean } = {}) => {
    const q = new URLSearchParams();
    if (opts.deferred) q.set("deferred", "1");
    if (opts.hidden) q.set("hidden", "1");
    return req<{ total: number; listed: number; tools: CatalogTool[] }>(`/api/catalog?${q}`);
  },

  primitives: () => req<any>("/api/primitives"),

  audit: (limit = 200) => req<{ entries: AuditRow[] }>(`/api/audit?limit=${limit}`),
  agents: () => req<{ agents: Array<{ name: string; scopes: string[] }> }>("/api/agents"),

  approveAll: () => req<{ pins: number }>("/api/pins/approve-all", { method: "POST" }),
  clearPins: () => req<{ pins: number }>("/api/pins/clear", { method: "POST" }),

  run: <T>(demo: string, body: Record<string, unknown> = {}) =>
    req<T>(`/api/run/${demo}`, { method: "POST", body: JSON.stringify(body) }),

  exfil: () => req<{ attempts: Array<{ ts: number; bytes: number; preview: string }> }>("/api/demo/exfil-sink"),
};

/* ------------------------------------------------------------- formatting */

export function fmtNumber(n: number): string {
  return n.toLocaleString("en-US");
}

export function fmtBytes(n: number): string {
  if (n < 1024) return `${n} B`;
  if (n < 1024 * 1024) return `${(n / 1024).toFixed(1)} KB`;
  return `${(n / 1024 / 1024).toFixed(2)} MB`;
}

export function fmtTime(ts: number): string {
  const d = new Date(ts);
  return `${String(d.getHours()).padStart(2, "0")}:${String(d.getMinutes()).padStart(2, "0")}:${String(
    d.getSeconds(),
  ).padStart(2, "0")}.${String(d.getMilliseconds()).padStart(3, "0")}`;
}

export function fmtCents(cents: number): string {
  return (cents / 100).toLocaleString("en-US", { style: "currency", currency: "USD" });
}
